import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Feature } from '../types/api';
import { llmService } from '../services/api';
import { useAuthToken } from '../hooks/useAuthToken';
import { PLACEHOLDERS } from '../utils/constants';
import { featureRefinementDialogStyles } from '../styles';

interface FeatureRefinementDialogProps {
  open: boolean;
  onClose: () => void;
  feature: Feature | null;
  requirements: string;
  onRefine: (originalDescription: string, newDescription: string) => void;
  loading?: boolean;
}

const FeatureRefinementDialog: React.FC<FeatureRefinementDialogProps> = ({
  open,
  onClose,
  feature,
  requirements,
  onRefine,
  loading = false,
}) => {
  const [refinementText, setRefinementText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refinedDescription, setRefinedDescription] = useState<string | null>(null);
  const [isRefining, setIsRefining] = useState(false);
  const { token } = useAuthToken();

  const handleClose = () => {
    setRefinementText('');
    setError(null);
    setRefinedDescription(null);
    onClose();
  };

  const handleRefine = async () => {
    if (!feature || !refinementText.trim() || !token) {
      setError('Пожалуйста, введите текст для доуточнения');
      return;
    }

    setError(null);
    setIsRefining(true);
    
    try {
      const response = await llmService.refineFeature({
        token,
        requirements,
        currentFeatureDescription: feature.featureDescription,
        featureText: refinementText,
      });
      
      setRefinedDescription(response.featureDescription);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при доуточнении');
    } finally {
      setIsRefining(false);
    }
  };

  const handleSave = () => {
    if (refinedDescription && feature) {
      onRefine(feature.featureDescription, refinedDescription);
      handleClose();
    }
  };

  if (!feature) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={featureRefinementDialogStyles.dialog}
      keepMounted
      scroll="paper"
    >
      <DialogTitle>
        Доуточнение фичи: {feature.featureName}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={featureRefinementDialogStyles.currentDescriptionContainer}>
          <Typography variant="subtitle2" sx={featureRefinementDialogStyles.currentDescriptionLabel}>
            Текущее описание:
          </Typography>
          <Typography variant="body2" sx={featureRefinementDialogStyles.currentDescriptionBox}>
            {feature.featureDescription}
          </Typography>
        </Box>

        <Divider sx={featureRefinementDialogStyles.divider} />

        <Box sx={featureRefinementDialogStyles.refinementContainer}>
          <Typography variant="subtitle2" sx={featureRefinementDialogStyles.refinementLabel}>
            Введите текст для доуточнения:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={refinementText}
            onChange={(e) => setRefinementText(e.target.value)}
            placeholder={PLACEHOLDERS.FEATURE_REFINEMENT}
            variant="outlined"
            disabled={loading || isRefining}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={featureRefinementDialogStyles.errorAlert}>
            {error}
          </Alert>
        )}

        {refinedDescription && (
          <Box sx={featureRefinementDialogStyles.newDescriptionContainer}>
            <Typography variant="subtitle2" sx={featureRefinementDialogStyles.newDescriptionLabel}>
              Новое описание:
            </Typography>
            <Typography variant="body2" sx={featureRefinementDialogStyles.newDescriptionBox}>
              {refinedDescription}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={loading || isRefining}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minHeight: { xs: '48px', sm: '36px' },
          }}
        >
          Отмена
        </Button>
        {!refinedDescription ? (
          <Button 
            onClick={handleRefine} 
            variant="contained" 
            disabled={loading || isRefining || !refinementText.trim() || !token}
            startIcon={isRefining ? <CircularProgress size={20} /> : null}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: { xs: '48px', sm: '36px' },
            }}
          >
            {isRefining ? 'Доуточняем...' : 'Доуточнить'}
          </Button>
        ) : (
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="success"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: { xs: '48px', sm: '36px' },
            }}
          >
            Сохранить изменения
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeatureRefinementDialog; 